/**
 * Agent editor (§6, §13) — mounted at /agents/new (create) and /agents/:id
 * (edit). Identity is always visible; Behavior, Voice, Conversation,
 * Knowledge, Telephony, and Multi-agent are collapsible and closed by
 * default — sections holding validation errors auto-open on save.
 *
 * Save: create POSTs then navigates to /agents/:id; edit PATCHes
 * optimistically with rollback + an inline error banner on failure.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { Collapsible } from "../components/Collapsible";
import { DeleteAgentModal } from "../components/DeleteAgentModal";
import { Field } from "../components/Field";
import { Input } from "../components/Input";
import { Select } from "../components/Select";
import { Spinner } from "../components/Spinner";
import { StatusDot, agentStatusTone } from "../components/StatusDot";
import { BehaviorSection } from "../components/agent-editor/BehaviorSection";
import { ConversationSection } from "../components/agent-editor/ConversationSection";
import { ENDPOINTING_PRESETS, LANGUAGE_OPTIONS, STARTER_VOICES } from "../components/agent-editor/constants";
import { KnowledgePanel } from "../components/agent-editor/KnowledgePanel";
import { MultiAgentSection } from "../components/agent-editor/MultiAgentSection";
import { TelephonySection } from "../components/agent-editor/TelephonySection";
import { Tooltip } from "../components/agent-editor/Tooltip";
import { VoiceSection } from "../components/agent-editor/VoiceSection";
import {
  contextBudgetWarning,
  draftFromAgent,
  newDraft,
  normalizeDraft,
  sectionsWithErrors,
  validateDraft,
  type AgentDraft,
  type DraftErrors,
  type SectionId,
} from "../components/agent-editor/validation";
import { api } from "../lib/api";
import { cx, formatPhone } from "../lib/format";
import type {
  Agent,
  AgentConfig,
  AgentStatus,
  AgentSummary,
  AgentWrite,
  DocumentInfo,
  PhoneNumber,
} from "../lib/types";
import { ENGINE_LABELS_SHORT } from "../lib/types";

type CollapsibleSectionId = Exclude<SectionId, "identity">;

const CLOSED_SECTIONS: Record<CollapsibleSectionId, boolean> = {
  behavior: false,
  voice: false,
  conversation: false,
  knowledge: false,
  telephony: false,
  multiagent: false,
};

const STATUS_OPTIONS: { value: AgentStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
];

export default function AgentEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  // ---------------------------------------------------------------- state
  const [draft, setDraft] = useState<AgentDraft | null>(isNew ? newDraft() : null);
  const [savedAgent, setSavedAgent] = useState<Agent | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [allAgents, setAllAgents] = useState<AgentSummary[] | null>(null);
  const [numbers, setNumbers] = useState<PhoneNumber[] | null>(null);
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);

  const [errors, setErrors] = useState<DraftErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<Record<CollapsibleSectionId, boolean>>(CLOSED_SECTIONS);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ---------------------------------------------------------------- loads
  useEffect(() => {
    if (isNew) {
      setDraft(newDraft());
      setSavedAgent(null);
      setErrors({});
      setSaveError(null);
      setOpen(CLOSED_SECTIONS);
      return;
    }
    const controller = new AbortController();
    setDraft(null);
    setLoadError(null);
    api.agents
      .get(id, controller.signal)
      .then((agent) => {
        setSavedAgent(agent);
        setDraft(draftFromAgent(agent));
        setErrors({});
        setSaveError(null);
        setOpen(CLOSED_SECTIONS);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(error instanceof Error ? error.message : "Could not load this agent.");
      });
    return () => controller.abort();
  }, [id, isNew, reloadKey]);

  useEffect(() => {
    const controller = new AbortController();
    api.agents
      .list(controller.signal)
      .then(setAllAgents)
      .catch(() => {
        if (!controller.signal.aborted) setAllAgents([]);
      });
    api.numbers
      .list(controller.signal)
      .then(setNumbers)
      .catch(() => {
        if (!controller.signal.aborted) setNumbers([]);
      });
    return () => controller.abort();
  }, [reloadKey]);

  const otherAgents = useMemo(
    () => (allAgents === null ? null : allAgents.filter((a) => a.id !== id)),
    [allAgents, id],
  );

  const onDocumentsChange = useCallback((docs: DocumentInfo[]) => setDocuments(docs), []);
  const readyKnowledgeTokens = useMemo(
    () =>
      documents
        .filter((d) => d.status === "ready")
        .reduce((sum, d) => sum + (d.token_count ?? 0), 0),
    [documents],
  );

  // ---------------------------------------------------------------- draft ops
  const patchConfig = useCallback((partial: Partial<AgentConfig>) => {
    setDraft((prev) => (prev ? { ...prev, config: { ...prev.config, ...partial } } : prev));
  }, []);

  function patchDraft(partial: Partial<Omit<AgentDraft, "config">>) {
    setDraft((prev) => (prev ? { ...prev, ...partial } : prev));
  }

  const dirty = useMemo(() => {
    if (!draft) return false;
    if (isNew) return true;
    if (!savedAgent) return false;
    return JSON.stringify(draft) !== JSON.stringify(draftFromAgent(savedAgent));
  }, [draft, savedAgent, isNew]);

  const budgetWarning = draft ? contextBudgetWarning(draft, readyKnowledgeTokens) : null;

  // ---------------------------------------------------------------- save
  async function syncNumberAssignment(
    agentId: string,
    nextNumberId: string | null,
    prevNumberId: string | null,
  ): Promise<string | null> {
    if (nextNumberId === prevNumberId) return null;
    try {
      if (prevNumberId) await api.numbers.assign(prevNumberId, null);
      if (nextNumberId) await api.numbers.assign(nextNumberId, agentId);
      setNumbers(await api.numbers.list());
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : "number assignment failed";
    }
  }

  async function handleSave() {
    if (!draft || saving) return;
    const normalized = normalizeDraft(draft);
    const validationErrors = validateDraft(normalized, otherAgents ?? []);
    setErrors(validationErrors);
    setDraft(normalized);

    if (Object.keys(validationErrors).length > 0) {
      // Open every section holding an error, then bring the first into view.
      const sections = sectionsWithErrors(validationErrors);
      setOpen((prev) => {
        const next = { ...prev };
        for (const section of sections) {
          if (section !== "identity") next[section] = true;
        }
        return next;
      });
      setSaveError("Some fields need attention — they're highlighted below.");
      window.setTimeout(() => {
        document.querySelector('[role="alert"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
      return;
    }

    setSaveError(null);
    const payload: AgentWrite = {
      name: normalized.name,
      description: normalized.description || null,
      status: normalized.status,
      role: normalized.role,
      config: normalized.config,
    };

    if (isNew) {
      setSaving(true);
      try {
        const created = await api.agents.create(payload);
        const numberProblem = await syncNumberAssignment(
          created.id,
          normalized.config.phone_number_id,
          null,
        );
        if (numberProblem) {
          setSaveError(`Agent created, but the phone number could not be assigned: ${numberProblem}`);
        }
        navigate(`/agents/${created.id}`, { replace: true });
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : "Could not create the agent.");
      } finally {
        setSaving(false);
      }
      return;
    }

    // Edit: optimistic — mark saved immediately, roll back on failure.
    const previous = savedAgent;
    if (!previous) {
      setSaveError("The agent hasn't finished loading yet — try again in a moment.");
      return;
    }
    const prevNumberId = previous.config.phone_number_id ?? null;
    const optimistic: Agent = {
      ...previous,
      name: normalized.name,
      description: normalized.description || null,
      status: normalized.status,
      role: normalized.role,
      config: normalized.config,
      updated_at: new Date().toISOString(),
    };
    setSavedAgent(optimistic);
    setSaving(true);
    try {
      const updated = await api.agents.update(id, payload);
      setSavedAgent(updated);
      // Adopt server truth unless the user kept typing during the round-trip.
      setDraft((current) =>
        current && JSON.stringify(current) === JSON.stringify(normalized)
          ? draftFromAgent(updated)
          : current,
      );
      const numberProblem = await syncNumberAssignment(
        id,
        normalized.config.phone_number_id,
        prevNumberId,
      );
      if (numberProblem) {
        setSaveError(`Saved, but the phone number assignment could not be updated: ${numberProblem}`);
      }
      // Keep the summary list (used for name/engine validation) in sync.
      setAllAgents(
        (prev) =>
          prev?.map((a) =>
            a.id === updated.id
              ? {
                  ...a,
                  name: updated.name,
                  description: updated.description,
                  status: updated.status,
                  role: updated.role,
                  voice_engine: updated.config.voice_engine,
                  updated_at: updated.updated_at,
                }
              : a,
          ) ?? prev,
      );
    } catch (error) {
      setSavedAgent(previous); // rollback — the draft keeps the user's edits
      setSaveError(
        error instanceof Error
          ? `Save failed — your changes are still here. ${error.message}`
          : "Save failed — your changes are still here.",
      );
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------- delete
  async function handleDelete() {
    if (!id || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.agents.remove(id);
      navigate("/agents");
    } catch (error) {
      setDeleteError(
        error instanceof Error
          ? `Could not delete the agent. ${error.message}`
          : "Could not delete the agent. Try again.",
      );
      setDeleting(false);
    }
  }

  // Cmd/Ctrl+S saves — this is an operator tool.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        void handleSave();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  // ---------------------------------------------------------------- render
  if (loadError) {
    return (
      <div className="flex flex-col items-start gap-4">
        <h1 className="font-display text-xl tracking-display text-bright">Agent editor</h1>
        <div className="flex w-full items-center justify-between rounded border border-danger/60 bg-danger/10 px-4 py-3">
          <p className="text-sm text-danger">{loadError}</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
            <Button size="sm" variant="ghost" onClick={() => navigate("/agents")}>
              Back to agents
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  const config = draft.config;
  const sectionToggle = (section: CollapsibleSectionId) => (isOpen: boolean) =>
    setOpen((prev) => ({ ...prev, [section]: isOpen }));

  // Collapsed one-line summaries — surface the defaults (§6 progressive disclosure).
  const voiceSummary =
    config.voice_engine === "realtime"
      ? `Realtime · ${config.realtime_voice}`
      : config.voice_engine === "gemini"
        ? `Natural · ${config.gemini_voice}`
        : `Standard · ${
            STARTER_VOICES[config.tts_provider].find((v) => v.id === config.voice_id)?.name ??
            (config.voice_id ? "custom voice" : "no voice picked")
          }`;
  const behaviorSummary = config.system_prompt.trim()
    ? `${config.system_prompt.length.toLocaleString()} chars of instructions · ${config.restrictions.length} restriction${config.restrictions.length === 1 ? "" : "s"}`
    : "Needs instructions — open to write the prompt";
  const conversationSummary =
    config.voice_engine === "realtime"
      ? `Interruptions ${config.allow_interruptions ? "on" : "off"} · turn detection: ${config.realtime_turn_detection === "semantic_vad" ? "semantic" : "silence-based"}`
      : config.voice_engine === "gemini"
        ? // Gemini does its own endpointing and exposes no knob for it.
          `Interruptions ${config.allow_interruptions ? "on" : "off"} · turn detection: built-in`
        : `Interruptions ${config.allow_interruptions ? "on" : "off"} · ${
            ENDPOINTING_PRESETS.find((p) => p.ms === config.endpointing_ms)?.label.toLowerCase() ??
            `${config.endpointing_ms}ms`
          } response speed`;
  const knowledgeSummary =
    documents.length === 0
      ? "No documents — the agent answers from its prompt alone"
      : `${documents.length} document${documents.length === 1 ? "" : "s"} · ${documents.filter((d) => d.status === "ready").length} ready`;
  const assignedNumber = (numbers ?? []).find((n) => n.id === config.phone_number_id);
  const telephonySummary = assignedNumber
    ? `${formatPhone(assignedNumber.e164)} · ${config.inbound_enabled ? "inbound on" : "inbound off"}`
    : "No number — browser test only";
  const multiagentSummary =
    draft.role === "standalone"
      ? "Standalone — no transfers"
      : `${draft.role === "router" ? "Router" : "Specialist"} · ${config.handoff_targets.length} transfer target${config.handoff_targets.length === 1 ? "" : "s"}`;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <Link
            to="/agents"
            className="text-xs text-muted underline-offset-4 transition-colors hover:text-bright hover:underline"
          >
            ← Agents
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="truncate font-display text-xl tracking-display text-bright">
              {isNew ? "New agent" : draft.name || "Untitled agent"}
            </h1>
            {savedAgent && (
              <StatusDot tone={agentStatusTone(savedAgent.status)} label={savedAgent.status} />
            )}
            <Badge mono>{ENGINE_LABELS_SHORT[config.voice_engine]}</Badge>
          </div>
        </div>
        {!isNew && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setDeleteError(null);
              setConfirmingDelete(true);
            }}
          >
            Delete agent
          </Button>
        )}
      </div>

      {budgetWarning && (
        <p className="rounded border border-warning/60 bg-warning/10 px-4 py-3 text-xs text-warning">
          {budgetWarning}
        </p>
      )}

      {/* §6.1 Identity — always visible */}
      <section className="rounded border border-line bg-surface p-4">
        <h2 className="mb-4 font-display text-base tracking-display text-bright">Identity</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Name"
            required
            error={errors.name}
            trailing={<span className="font-mono">{draft.name.length} / 60</span>}
            help="For your dashboard only — never sent to the model."
          >
            <Input
              value={draft.name}
              placeholder="e.g. Front-desk assistant"
              onChange={(event) => patchDraft({ name: event.target.value })}
            />
          </Field>
          <Field label="Language" help="Drives speech recognition and voice filtering.">
            <Select
              value={config.language}
              options={LANGUAGE_OPTIONS}
              onChange={(event) => patchConfig({ language: event.target.value })}
            />
          </Field>
          <Field
            label="Status"
            help="Disabled agents reject all calls. Draft and active agents can take calls and be tested."
          >
            <Select
              value={draft.status}
              options={STATUS_OPTIONS}
              onChange={(event) => patchDraft({ status: event.target.value as AgentStatus })}
            />
          </Field>
          <Field
            label="Description"
            error={errors.description}
            trailing={<span className="font-mono">{draft.description.length} / 200</span>}
            help="A note to your future self."
            className="sm:col-span-2"
          >
            <Input
              value={draft.description}
              placeholder="What is this agent for?"
              onChange={(event) => patchDraft({ description: event.target.value })}
            />
          </Field>
        </div>
      </section>

      {/* Collapsible sections — closed by default (§6 progressive disclosure) */}
      <Collapsible
        title="Behavior"
        subtitle={behaviorSummary}
        open={open.behavior}
        onOpenChange={sectionToggle("behavior")}
      >
        <BehaviorSection config={config} patch={patchConfig} errors={errors} />
      </Collapsible>

      <Collapsible
        title="Voice"
        subtitle={voiceSummary}
        open={open.voice}
        onOpenChange={sectionToggle("voice")}
      >
        <VoiceSection
          config={config}
          patch={patchConfig}
          errors={errors}
          readyKnowledgeTokens={readyKnowledgeTokens}
        />
      </Collapsible>

      <Collapsible
        title="Conversation"
        subtitle={conversationSummary}
        open={open.conversation}
        onOpenChange={sectionToggle("conversation")}
      >
        <ConversationSection config={config} patch={patchConfig} errors={errors} />
      </Collapsible>

      <Collapsible
        title="Knowledge"
        subtitle={knowledgeSummary}
        open={open.knowledge}
        onOpenChange={sectionToggle("knowledge")}
      >
        <KnowledgePanel
          agentId={id ?? null}
          config={config}
          patch={patchConfig}
          onDocumentsChange={onDocumentsChange}
        />
      </Collapsible>

      <Collapsible
        title="Phone"
        subtitle={telephonySummary}
        open={open.telephony}
        onOpenChange={sectionToggle("telephony")}
      >
        <TelephonySection
          config={config}
          patch={patchConfig}
          errors={errors}
          numbers={numbers}
          agentId={id ?? null}
          dirty={dirty}
        />
      </Collapsible>

      <Collapsible
        title="Multiple agents"
        subtitle={multiagentSummary}
        open={open.multiagent}
        onOpenChange={sectionToggle("multiagent")}
      >
        <MultiAgentSection
          config={config}
          patch={patchConfig}
          errors={errors}
          role={draft.role}
          onRoleChange={(role) => patchDraft({ role })}
          otherAgents={otherAgents}
        />
      </Collapsible>

      {/* Sticky footer — Save + Test agent (§13 sticky "Test agent" button) */}
      <div className="sticky bottom-0 z-20 -mx-6 -mb-6 mt-2 border-t border-line bg-ink/95 px-6 py-3 backdrop-blur">
        {saveError && (
          <p className="mb-2 rounded border border-danger/60 bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
            {saveError}
          </p>
        )}
        <div className="flex items-center justify-between gap-4">
          <span
            className={cx(
              "text-xs",
              saving ? "text-body" : dirty ? "text-warning" : "text-muted",
            )}
          >
            {saving
              ? "Saving…"
              : isNew
                ? "Not created yet"
                : dirty
                  ? "Unsaved changes"
                  : "All changes saved"}
          </span>
          <div className="flex items-center gap-2">
            <Tooltip
              content="Save the agent first — you can talk to it right after."
              disabled={!isNew}
              side="top"
            >
              <Button
                disabled={isNew}
                onClick={() => id && navigate(`/agents/${id}/test`)}
              >
                Test agent
              </Button>
            </Tooltip>
            <Button variant="primary" loading={saving} onClick={() => void handleSave()}>
              {isNew ? "Create agent" : "Save changes"}
            </Button>
          </div>
        </div>
      </div>

      <DeleteAgentModal
        open={confirmingDelete}
        agentName={draft.name || "this agent"}
        deleting={deleting}
        error={deleteError}
        onCancel={() => {
          setConfirmingDelete(false);
          setDeleteError(null);
        }}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
}
